import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { useRefetchOnActive } from '../../../shared/hooks/useRefetchOnActive';
import PayoutForm from '../../payout-confirmation/components/PayoutForm';
import StartCycleFlow from '../../cycle-engine/components/StartCycleFlow';
import ReportProblemButton from '../../support/components/ReportProblemButton';
import MemberSubmissionReviewModal from './MemberSubmissionReviewModal';
import MemberLateFees from './MemberLateFees';
import { fetchGroupFees } from '../../../shared/utils/lateFees';
import {
  PAYMENT_STATUSES,
  PAYMENT_STATUS_CLASSES,
  PAYMENT_STATUS_LABELS,
} from '../../../shared/constants/paymentStatuses';
import {
  canSendPayout as canSendPayoutForHand,
  getRoundPayment,
  getLiveRound,
  isCycleComplete,
  getCollectionPosition,
  adminAdvanceRound,
} from '../../cycle-engine';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';

const GroupPaymentDashboard = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [groupData, setGroupData] = useState(null);
  const [groupFees, setGroupFees] = useState([]);  // late fees (shaped); separate from pot/contributions
  const [members, setMembers] = useState([]);
  const [hands, setHands] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHand, setSelectedHand] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showReadOnly, setShowReadOnly] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [voidConfirm, setVoidConfirm] = useState(false);  // two-step void on the read-only modal
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState(null);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [showStartCycle, setShowStartCycle] = useState(false);
  const [startCycleMode, setStartCycleMode] = useState('first');
  const [selectedPayoutHand, setSelectedPayoutHand] = useState(null);
  const [showAssignHandsModal, setShowAssignHandsModal] = useState(false);
  const [assigningMember, setAssigningMember] = useState(null);
  const [assignHandsForm, setAssignHandsForm] = useState({ numberOfHands: '', positions: [] });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewHand, setReviewHand] = useState(null);
  const [reviewPayment, setReviewPayment] = useState(null);
  const [reviewMember, setReviewMember] = useState(null);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [susuOn, setSusuOn] = useState(false);
  const [advancingPayout, setAdvancingPayout] = useState(null);
  const [advanceNote, setAdvanceNote] = useState('');
  const [formData, setFormData] = useState({
    amountReceived: '',
    paymentMethod: '',
    referenceNumber: '',
    dateReceived: new Date().toISOString().split('T')[0],
  });
  // Record-Payment UX: no defaulted paid-by. The admin picks an explicit action
  // (member payment vs cover) which opens a confirmation summary; nothing writes
  // until Confirm. pendingSubmit holds the validated draft { paidBy, ...fields }.
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [formError, setFormError] = useState(null);
  const [recording, setRecording] = useState(false);

  // In-flight guard: focus + visibility can fire together — collapse overlap.
  const fetchingRef = useRef(false);
  const fetchData = async ({ silent = false } = {}) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!silent) setLoading(true);  // silent = background refetch, no spinner
      const numericGroupId = parseInt(groupId, 10);
      const { data: group } = await supabaseClient.from('groups').select('*').eq('id', numericGroupId).single();

      const { data: membersData } = await supabaseClient
        .from('members').select('*').eq('group_id', numericGroupId).eq('status', 'active');
      const { data: handsData } = await supabaseClient.from('hands').select('*').eq('group_id', numericGroupId);
      const { data: paymentsData } = await supabaseClient.from('payments').select('*').eq('group_id', numericGroupId);
      const { data: roundsData } = await supabaseClient.from('rounds').select('*').eq('group_id', numericGroupId);
      const { data: payoutsData } = await supabaseClient.from('payouts').select('*').eq('group_id', numericGroupId);
      setGroupData(group);
      // Late fees live in their own tables (never in pot/contribution math). Only
      // load them when the group has fees enabled.
      setGroupFees(group?.fees_enabled ? await fetchGroupFees(numericGroupId) : []);
      setMembers(membersData || []);
      setHands(handsData || []);
      setPayments(paymentsData || []);
      setRounds(roundsData || []);
      setPayouts(payoutsData || []);
      setSusuOn(await checkFeatureFlag(FEATURE_FLAGS.SUSU_ROUNDS));

      // Fetch unread admin notifications for THIS group only (Stage 41 group_id).
      // Scoped so a per-group dashboard never shows another group's notifications.
      try {
        const { data: { user } } = await supabaseClient.auth.getUser();
        if (user) {
          const { data: notifData } = await supabaseClient
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .eq('group_id', numericGroupId)
            .eq('read', false)
            .order('created_at', { ascending: false });
          setAdminNotifications(notifData || []);
        }
      } catch {
        setAdminNotifications([]);
      }
    } catch (err) {
      setError('Failed to load payment data');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => { if (groupId) fetchData(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the tab/window regains focus or becomes visible again (replaces
  // the former payments-only Realtime channel; this re-pulls the whole view).
  useRefetchOnActive(() => { if (groupId) fetchData({ silent: true }); });

  // Derive active-member hands for totals and payout eligibility — excludes removed member hands
  const activeMemberIds = new Set(members.map(m => m.id));
  const activeHands = hands.filter(h => activeMemberIds.has(h.member_db_id) && h.is_active !== false);
  const activeHandIds = new Set(activeHands.map(h => h.id));

  // Live round (susu_rounds) — the authoritative source of turn/funding state.
  const currentRound = getLiveRound(rounds, parseInt(groupId, 10));

  // Round-scoped payment lookup — the SAME source the per-hand statuses use, so
  // the summary counters can never drift from the rows below.
  const getHandPayment = (hand) => getRoundPayment(hand, currentRound, payments);

  // Model B: the hand that collects THIS round contributes nothing this round.
  // Single source of truth for "is this hand the current round's collector",
  // reused by the summary tiles (Total Expected = N-1), the per-hand row (no
  // Record Payment, "Collecting" state), and the payout gate.
  const isCollectorThisRound = (hand) =>
    susuOn && !!currentRound && String(hand.id) === String(currentRound.collector_hand_id);

  // Summary counters scope to the CURRENT round only (susu_rounds). Each hand owes
  // one contribution per round, so Total Expected is the per-round pot; Total
  // Collected sums only THIS round's paid/covered payments (via getHandPayment) —
  // never carrying over a prior round. On round advance the live round changes and
  // the counters reset for it. Flag-off (legacy) keeps the cumulative behavior.
  const summaryPayments = (susuOn && currentRound)
    ? activeHands.map(h => getHandPayment(h)).filter(Boolean)
    : payments.filter(p => activeHandIds.has(p.hand_id));

  // Model B: exclude the current round's collector — they owe no contribution
  // this round, so the round's expected total is the OTHERS (N-1). Matches the
  // N-1 funding gate, the payout amount, and Total Collected (the collector has
  // no payment row). Guard keeps legacy all-N behavior pre-cycle / flag-off.
  const totalExpected = activeHands
    .filter(h => !isCollectorThisRound(h))
    .reduce((sum, h) => sum + (h.contribution_amount || 0), 0);
  const totalCollected = summaryPayments
    .filter(p => p.status === 'paid' || p.status === 'covered')
    .reduce((sum, p) => sum + (p.amount_received || 0), 0);
  const remaining = totalExpected - totalCollected;

  // Terminal state: cycle finished (no live round + all positions collected). The
  // per-round counters are meaningless here — show a Cycle Complete summary and
  // mark every hand collected instead of summing/pending.
  const cycleComplete = susuOn && isCycleComplete(rounds, parseInt(groupId, 10));
  const completedCycleNumber = rounds.length
    ? Math.max(...rounds.map(r => Number(r.cycle_number)))
    : (groupData?.cycle_number || 1);
  // Total distributed this cycle. OR pattern (provably Model-B-safe): sum the
  // confirmed/advanced payout amounts when payouts exist (Model B — unchanged),
  // else sum the ACTUAL confirmed contributions across the cycle's confirmed rounds
  // for direct groups, which have no payouts. Group-wide `payments` is in scope, so
  // the admin figure sums real amounts (exact even if a contribution is short). The
  // collector's own row stays 'pending' in its round, so paid/covered excludes it.
  const byPayoutDistributed = payouts
    .filter(p => Number(p.cycle_number) === completedCycleNumber && ['confirmed', 'advanced_unconfirmed'].includes(p.status))
    .reduce((sum, p) => sum + (p.payout_amount || 0), 0);
  const completedConfirmedRoundIds = new Set(
    rounds
      .filter(r => Number(r.cycle_number) === completedCycleNumber && r.status === 'confirmed')
      .map(r => String(r.id))
  );
  const byPaymentsDistributed = payments
    .filter(p => completedConfirmedRoundIds.has(String(p.round_id)) && ['paid', 'covered'].includes(p.status))
    .reduce((sum, p) => sum + (Number(p.amount_received) || 0), 0);
  const totalDistributed = byPayoutDistributed > 0 ? byPayoutDistributed : byPaymentsDistributed;

  // Order the roster by COLLECTION order (round_number), not assignment position.
  const effectiveCycle = currentRound?.cycle_number ?? completedCycleNumber;
  // Members actually IN the effective cycle (have a collector-round). Excludes
  // hands added between cycles, so the completed-cycle banner doesn't overstate.
  const inCycleMemberCount = activeHands.filter(
    h => getCollectionPosition(h, rounds, effectiveCycle) != null
  ).length;
  const memberCollectionRank = (member) => {
    const ids = new Set(hands.filter(h => h.member_db_id === member.id && h.is_active !== false).map(h => String(h.id)));
    const ranks = rounds
      .filter(r => Number(r.cycle_number) === effectiveCycle && ids.has(String(r.collector_hand_id)))
      .map(r => Number(r.round_number));
    return ranks.length ? Math.min(...ranks) : Infinity;
  };
  const orderedMembers = [...members].sort((a, b) => memberCollectionRank(a) - memberCollectionRank(b));
  const getHandStatus = (hand) => getHandPayment(hand)?.status || 'pending';

  // ── Add-Late-Fee availability ────────────────────────────────────────────
  // A late fee is only meaningful once a member's contribution is actually LATE:
  // there must be a running cycle, the member must owe on the live round, and the
  // round's due date must have passed. This is pure UI-availability logic — it
  // never touches the cycle/rotation engine. Statuses that count as "still owes"
  // are pending / late / rejected; paid / covered / member_submitted are excluded.
  const UNMET_STATUSES = new Set([
    PAYMENT_STATUSES.PENDING,
    PAYMENT_STATUSES.LATE,
    PAYMENT_STATUSES.REJECTED,
  ]);
  // (c) Past the due date. Compare calendar days in local time so a due date of
  // "today" is NOT yet late (late begins the day after). null due date → unknown,
  // returned as null so the caller falls back to (a)+(b) and missing data never
  // blocks the admin.
  const isRoundPastDue = (() => {
    const due = currentRound?.payment_due_date;
    if (!due) return null;
    const iso = typeof due === 'string' ? due.slice(0, 10) : new Date(due).toISOString().slice(0, 10);
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    const dueDay = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDay < today;
  })();
  // Full gate: (a) cycle active, (b) this member owes on the live round,
  // (c) past due (null due date drops (c), keeping (a)+(b)).
  const canAddLateFee = (memberHandsForMember) => {
    if (!(susuOn && currentRound)) return false; // (a)
    const owes = memberHandsForMember.some(h => UNMET_STATUSES.has(getHandStatus(h))); // (b)
    if (!owes) return false;
    return isRoundPastDue !== false; // (c): past-due OR unknown due date
  };

  // Payout eligibility now comes from the shared resolver: current round funded,
  // this hand is the collector, no payout yet. Flag-gated so it stays inert until on.
  const canSendPayout = (hand) => susuOn && canSendPayoutForHand(hand, rounds, payments, payouts);

  // Admin-advance fallback: shown when the live round is paid_out but its payout
  // is still pending_confirmation (collector hasn't confirmed). Hidden when the
  // payout is disputed or confirmed.
  const liveRoundPayout = currentRound
    ? payouts.find(p => String(p.round_id) === String(currentRound.id))
    : null;
  const canAdminAdvance =
    susuOn && currentRound?.status === 'paid_out' && liveRoundPayout?.status === 'pending_confirmation';
  const collectorName = currentRound
    ? (members.find(m => String(m.id) === String(currentRound.collector_member_id))?.name || 'The collector')
    : '';
  const hoursSincePayout = liveRoundPayout?.created_at
    ? Math.floor((Date.now() - new Date(liveRoundPayout.created_at)) / (1000 * 60 * 60))
    : 0;
  const escalateAdvance = hoursSincePayout >= 48;
  // Terminal round: the last round of the cycle has no next round to advance to —
  // confirming it COMPLETES the cycle. Drives the terminal vs mid-cycle wording.
  const isFinalRound = !!currentRound && Number(currentRound.round_number) === Number(currentRound.contributor_count);

  const handleAdminAdvance = async () => {
    try {
      await adminAdvanceRound(advancingPayout.id, advanceNote.trim() || null);
      setAdvancingPayout(null);
      setAdvanceNote('');
      await fetchData();
    } catch (err) {
      alert(err.message || 'Failed to advance the round.');
    }
  };

  const handleHandClick = (hand) => {
    // No live round (before cycle start OR after complete) => payments are closed;
    // the per-hand Confirm Payment action is unavailable until a round is open.
    if (susuOn && !currentRound) return;
    // Model B: the round's collector owes no contribution — no Record Payment.
    if (isCollectorThisRound(hand)) return;
    const status = getHandStatus(hand);
    if (status === PAYMENT_STATUSES.MEMBER_SUBMITTED) {
      setReviewHand(hand);
      setReviewPayment(getHandPayment(hand));
      setReviewMember(members.find(m => m.id === hand.member_db_id) || null);
      setShowReviewModal(true);
    } else if (status === PAYMENT_STATUSES.PAID || status === PAYMENT_STATUSES.COVERED) {
      setSelectedPayment(getHandPayment(hand));
      setVoidConfirm(false);
      setVoidError(null);
      setShowReadOnly(true);
    } else {
      setSelectedHand(hand);
      setFormData({
        amountReceived: hand.contribution_amount || '',
        paymentMethod: '',
        referenceNumber: '',
        dateReceived: new Date().toISOString().split('T')[0],
      });
      setPendingSubmit(null);
      setFormError(null);
      setShowForm(true);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedHand(null);
    setPendingSubmit(null);
    setFormError(null);
  };

  const closeReadOnly = () => {
    setShowReadOnly(false);
    setSelectedPayment(null);
    setVoidConfirm(false);
    setVoidError(null);
  };

  // Void a mistaken payment via the guarded RPC (admin-only, blocked once the
  // round has paid out; hard-deletes + writes a payment_voided audit row).
  const handleVoidPayment = async () => {
    if (!selectedPayment) return;
    setVoiding(true);
    setVoidError(null);
    try {
      const { error } = await supabaseClient.rpc('void_payment', { p_payment_id: selectedPayment.id });
      if (error) throw error;
      closeReadOnly();
      await fetchData();
    } catch (err) {
      console.error('Void error:', err);
      setVoidError(err.message || 'Failed to void the payment.');
    } finally {
      setVoiding(false);
    }
  };

  // Step 1: validate the draft for the CHOSEN action, then open the confirmation
  // summary. Member payment mirrors the member side (method + reference required);
  // "covered by admin" is an owes-admin record with no external transaction, so
  // both are optional. Nothing is written here.
  const reviewPaymentDraft = (paidBy) => {
    setFormError(null);
    const amount = parseFloat(formData.amountReceived);
    if (!formData.amountReceived || Number.isNaN(amount) || amount <= 0) {
      setFormError('Enter a valid amount received.'); return;
    }
    if (!formData.dateReceived) { setFormError('Date received is required.'); return; }
    if (paidBy === 'member') {
      if (!formData.paymentMethod) {
        setFormError('Payment method is required for a member payment.'); return;
      }
      if (!formData.referenceNumber.trim()) {
        setFormError('Reference number is required for a member payment.'); return;
      }
    }
    setPendingSubmit({ paidBy, ...formData });
  };

  // Step 2: write the confirmed draft (payment upsert + audit row).
  const doRecordPayment = async () => {
    const p = pendingSubmit;
    if (!p) return;
    setFormError(null);
    // No live round (e.g. cycle complete) => payments are closed. Guard at the
    // write, not just the button, so there's no path to an orphan payment.
    if (susuOn && !currentRound) {
      setFormError('No active round is open — this cycle is closed to payments.'); return;
    }
    setRecording(true);
    try {
      const isCovered = p.paidBy === 'admin';
      const { data: { user } } = await supabaseClient.auth.getUser();
      const { error: paymentError } = await supabaseClient.from('payments').upsert({
        group_id: parseInt(groupId, 10),
        hand_id: selectedHand.id,
        amount_expected: selectedHand.contribution_amount,
        amount_received: parseFloat(p.amountReceived),
        payment_method: p.paymentMethod || null,
        payment_date: p.dateReceived,
        reference_number: p.referenceNumber?.trim() || null,
        status: isCovered ? 'covered' : 'paid',
        covered_by_admin: isCovered,
        cycle_number: currentRound?.cycle_number ?? groupData?.cycle_number ?? 1,
        round_number: currentRound?.round_number ?? null,
        round_id: currentRound?.id ?? null,
      }, { onConflict: 'hand_id,cycle_number,round_number' });
      if (paymentError) throw paymentError;
      await supabaseClient.from('audit_logs').insert({
        event_category: 'financial',
        event_type: 'payment_confirmed',
        actor_id: user.id,
        actor_role: 'admin',
        group_id: parseInt(groupId, 10),
        new_value: { amount_received: p.amountReceived, payment_method: p.paymentMethod || null, covered_by_admin: isCovered },
        risk_level: isCovered ? 'high' : 'low',
        flagged: isCovered,
        timestamp: new Date().toISOString()
      });
      closeForm();
      await fetchData();
    } catch (err) {
      console.error('Payment error:', err);
      setFormError('Failed to record the payment. Please try again.');
    } finally {
      setRecording(false);
    }
  };

  const handleAssignHands = async () => {
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const { data: groupInfo } = await supabaseClient
        .from('groups')
        .select('contribution_amount_per_hand')
        .eq('id', parseInt(groupId, 10))
        .single();
      const handsToInsert = assignHandsForm.positions.map(position => ({
        group_id: parseInt(groupId, 10),
        member_id: null,
        member_db_id: assigningMember.id,
        position_number: parseInt(position, 10),
        contribution_amount: groupInfo.contribution_amount_per_hand || 0,
        // contribution_status removed — per-round pending state lives in payments (susu_rounds).
      }));
      await supabaseClient.from('hands').insert(handsToInsert);
      await supabaseClient.from('audit_logs').insert({
        event_category: 'hands',
        event_type: 'hands_assigned',
        actor_id: user?.id || null,
        actor_role: 'admin',
        new_value: {
          member_id: assigningMember.id,
          member_name: assigningMember.name,
          positions: assignHandsForm.positions,
          group_id: parseInt(groupId, 10)
        },
        risk_level: 'low',
        flagged: false,
        timestamp: new Date().toISOString()
      });
      setShowAssignHandsModal(false);
      setAssigningMember(null);
      await fetchData();
    } catch (err) {
      console.error('Error assigning hands:', err);
      alert('Failed to assign hands. Please try again.');
    }
  };

  const handleDismissAdminNotification = async (notifId) => {
    try {
      await supabaseClient.from('notifications').update({ read: true }).eq('id', notifId);
      setAdminNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch {}
  };

  const statusClass = (status) => PAYMENT_STATUS_CLASSES[status] || 'bg-warning-100 text-warning-800';
  const statusLabel = (status) => PAYMENT_STATUS_LABELS[status] || (status?.toUpperCase() ?? 'PENDING');

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-center text-error-600">{error}</div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Admin in-app notifications */}
        {adminNotifications.length > 0 && (
          <div className="mb-4 space-y-2">
            {adminNotifications.map(notif => (
              <div
                key={notif.id}
                className="flex items-start justify-between bg-accent-50 border border-accent-200 rounded-lg px-4 py-3"
              >
                <p className="text-sm font-medium text-accent-800">{notif.message}</p>
                <button
                  onClick={() => handleDismissAdminNotification(notif.id)}
                  className="ml-3 flex-shrink-0 text-neutral-400 hover:text-neutral-600 text-lg leading-none"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
          <div>
            <h1 className="text-xl font-bold text-neutral-900">{groupData?.group_name}</h1>
            <p className="text-sm text-neutral-600">
              Payment Tracking — Cycle {groupData?.cycle_number || 1}
              {susuOn && currentRound ? ` · Round ${currentRound.round_number} of ${currentRound.contributor_count}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/admin/group/${groupId}/rotation`)}
              className="px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm hover:bg-primary-700"
            >
              View Rotation
            </button>
            <button onClick={() => navigate('/how-to')} className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">
              How It Works
            </button>
            <button onClick={() => navigate('/faq')} className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">
              Help / FAQ
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">
              ← Back
            </button>
          </div>
        </div>

        {/* Start Cycle 1 — first-ever in-app open_first_round caller. COPY: provisional */}
        {susuOn && rounds.length === 0 && activeHands.length >= 2 && (
          <div className="mb-6 rounded-lg p-5 border border-primary-500 bg-primary-50 text-center">
            <p className="text-lg font-bold text-primary-800">Ready to start Cycle 1</p>
            <p className="text-sm text-primary-700 mt-1 mb-3">
              {activeHands.length} active members · set the order and schedule to begin.
            </p>
            <button
              onClick={() => { setStartCycleMode('first'); setShowStartCycle(true); }}
              className="px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Start Cycle 1
            </button>
          </div>
        )}

        {cycleComplete ? (
          <div className="mb-6 rounded-lg p-5 border border-success-500 bg-success-50 text-center">
            <p className="text-2xl mb-1" aria-hidden="true">🎉</p>
            <p className="text-lg font-bold text-success-800">Cycle {completedCycleNumber} Complete</p>
            <p className="text-sm text-success-700 mt-1">
              All {inCycleMemberCount} members have collected · ${totalDistributed.toFixed(2)} distributed.
            </p>
            {/* COPY: provisional */}
            <button
              onClick={() => { setStartCycleMode('new'); setShowStartCycle(true); }}
              className="mt-4 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Start New Cycle
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-neutral-100 rounded-lg p-4 shadow-sm border border-neutral-200">
              <p className="text-xs text-neutral-600">Total Expected</p>
              <p className="text-lg font-bold text-primary-600">${totalExpected.toFixed(2)}</p>
            </div>
            <div className="bg-neutral-100 rounded-lg p-4 shadow-sm border border-neutral-200">
              <p className="text-xs text-neutral-600">Total Collected</p>
              <p className="text-lg font-bold text-success-600">${totalCollected.toFixed(2)}</p>
            </div>
            <div className="bg-neutral-100 rounded-lg p-4 shadow-sm border border-neutral-200">
              <p className="text-xs text-neutral-600">Remaining</p>
              <p className="text-lg font-bold text-warning-600">${remaining.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Admin fallback: advance a stuck round when the collector won't confirm */}
        {canAdminAdvance && (
          <div className={`mb-6 rounded-lg p-4 border ${escalateAdvance ? 'border-warning-500 bg-warning-50' : 'border-neutral-200 bg-neutral-100'}`}>
            <p className="text-sm font-medium text-neutral-900">
              {collectorName} hasn't confirmed receipt of their ${liveRoundPayout?.payout_amount?.toFixed(2)} payout
              {escalateAdvance ? ` — sent ${hoursSincePayout}h ago` : ''}.
            </p>
            <p className="text-xs text-neutral-600 mt-1">
              {isFinalRound
                ? 'This is the final round. The cycle completes once they confirm receipt.'
                : `Round ${currentRound?.round_number} can't advance until they confirm receipt.`}
            </p>
            <button
              onClick={() => setAdvancingPayout(liveRoundPayout)}
              className={`mt-3 px-3 py-2 rounded-lg text-sm font-medium ${escalateAdvance ? 'bg-warning-600 text-white hover:bg-warning-700' : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50'}`}
            >
              {isFinalRound ? 'Complete cycle' : 'Advance round'}
            </button>
          </div>
        )}

        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200">
          <div className="flex justify-between items-center px-4 py-3 border-b border-neutral-200">
            <h2 className="font-semibold text-neutral-900">Payment Status</h2>
            <button onClick={() => navigate(`/admin/group/${groupId}/add-member`)} className="px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm">
              + Add Member
            </button>
          </div>

          <div className="divide-y divide-neutral-200">
            {orderedMembers.map((member) => {
              const memberHands = hands
                .filter(h => h.member_db_id === member.id && h.is_active !== false)
                .sort((a, b) =>
                  (getCollectionPosition(a, rounds, effectiveCycle) ?? a.position_number)
                  - (getCollectionPosition(b, rounds, effectiveCycle) ?? b.position_number));
              return (
                <div key={member.id} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-medium text-neutral-900">{member.name}</h3>
                      <p className="text-xs text-neutral-400 mt-0.5">
                        {[member.email, member.whatsapp_number].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-neutral-500">{memberHands.length} hand(s)</span>
                      {memberHands.length === 0 && (() => {
                        // Roster is fixed at cycle start: assigning a hand while a
                        // cycle is running would inject an un-back-filled active hand
                        // (no rotation slot, not in contributor_count/pot, yet owed on
                        // future rounds). Disable mid-cycle; re-inclusion happens at
                        // the next cycle's roster step (StartCycleFlow). Enabled between
                        // cycles (no live round) — the legitimate roster-selection time.
                        const cycleActive = susuOn && !!currentRound;
                        return (
                          <button
                            onClick={() => {
                              setAssigningMember(member);
                              setAssignHandsForm({ numberOfHands: '', positions: [] });
                              setShowAssignHandsModal(true);
                            }}
                            disabled={cycleActive}
                            title={cycleActive ? 'Add members at the start of the next cycle.' : undefined}
                            className={`text-xs px-2 py-1 rounded ${cycleActive
                              ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                              : 'bg-warning-100 text-warning-700 hover:bg-warning-200'}`}
                          >
                            Assign Hands
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {memberHands.map((hand) => {
                      const status = getHandStatus(hand);
                      const collectionPos = getCollectionPosition(hand, rounds, effectiveCycle);
                      const noLiveRound = susuOn && !currentRound;
                      const collecting = isCollectorThisRound(hand);
                      return (
                        <div
                          key={hand.id}
                          onClick={() => handleHandClick(hand)}
                          className={`flex items-center justify-between p-3 bg-neutral-50 rounded-lg ${(noLiveRound || collecting) ? '' : 'cursor-pointer hover:bg-neutral-100'}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-neutral-900">Position {collectionPos ?? hand.position_number}</p>
                            {/* Model B: the collector owes no contribution this round —
                                show their collect state, not a "$X owed" line. */}
                            {collecting ? (
                              <p className="text-sm text-primary-600">Collecting this round — no contribution</p>
                            ) : (
                              <p className="text-sm text-neutral-600">${hand.contribution_amount?.toFixed(2)}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Badge precedence (Model B): a member NOT in this cycle's
                                rotation joins the next; a completed cycle shows Collected;
                                the live collector shows Collecting; otherwise the CURRENT
                                round's status (so a collector who submitted for the next
                                round shows "Submitted", not a sticky "Collected"). */}
                            {susuOn && getCollectionPosition(hand, rounds, effectiveCycle) == null ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-200 text-neutral-700">
                                Joins next cycle
                              </span>
                            ) : cycleComplete ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800">
                                ✓ Collected
                              </span>
                            ) : collecting ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                                Collecting
                              </span>
                            ) : (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass(status)}`}>
                                {statusLabel(status)}
                              </span>
                            )}
                            {canSendPayout(hand) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedPayoutHand(hand); setShowPayoutForm(true); }}
                                className="px-3 py-1 bg-primary-600 text-neutral-50 rounded-lg text-xs"
                              >
                                Send Payout
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {groupData?.fees_enabled && (
                    <MemberLateFees
                      member={member}
                      group={groupData}
                      fees={groupFees.filter(f => String(f.member_db_id) === String(member.id))}
                      canAdd={canAddLateFee(memberHands)}
                      onChanged={fetchData}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>


        {showForm && selectedHand && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
              <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{pendingSubmit ? 'Confirm Payment' : 'Record Payment'}</h3>
                <button onClick={closeForm} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
              </div>

              {!pendingSubmit ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Amount Received</label>
                    <input type="number" value={formData.amountReceived} onChange={e => setFormData({...formData, amountReceived: e.target.value})} className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
                    <select value={formData.paymentMethod} onChange={e => setFormData({...formData, paymentMethod: e.target.value})} className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base">
                      <option value="">Select method</option>
                      <option value="zelle">Zelle</option>
                      <option value="cashapp">Cash App</option>
                      <option value="venmo">Venmo</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Reference Number <span className="text-neutral-400 font-normal">(required for member payments)</span>
                    </label>
                    <input type="text" value={formData.referenceNumber} onChange={e => setFormData({...formData, referenceNumber: e.target.value})} className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Date Received</label>
                    <input type="date" value={formData.dateReceived} onChange={e => setFormData({...formData, dateReceived: e.target.value})} className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base" />
                  </div>

                  {formError && <p className="text-sm text-error-600">{formError}</p>}

                  {/* Two explicit actions — no defaulted choice. Each validates its
                      own required fields, then opens the confirmation summary. */}
                  <div className="space-y-2 pt-1">
                    <button type="button" onClick={() => reviewPaymentDraft('member')} className="w-full bg-primary-600 text-neutral-50 rounded-lg px-4 py-3 text-base font-medium">
                      Record member payment
                    </button>
                    <button type="button" onClick={() => reviewPaymentDraft('admin')} className="w-full border border-warning-500 bg-warning-50 text-warning-800 rounded-lg px-4 py-3 text-base font-medium">
                      Cover for member
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500">
                    <strong>Record member payment</strong> logs a payment the member made to you (reference required).{' '}
                    <strong>Cover for member</strong> records that you are fronting this contribution — an owes-admin
                    entry with no external transaction.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-neutral-600">Member</span><span className="font-medium">{members.find(m => m.id === selectedHand.member_db_id)?.name || 'Member'}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-600">Amount</span><span className="font-medium">${parseFloat(pendingSubmit.amountReceived || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-600">Method</span><span className="font-medium capitalize">{pendingSubmit.paymentMethod || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-600">Date</span><span className="font-medium">{pendingSubmit.dateReceived}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-neutral-600 flex-shrink-0">Reference</span><span className="font-medium text-right break-all min-w-0">{pendingSubmit.referenceNumber?.trim() || '—'}</span></div>
                    <div className="flex justify-between pt-1 border-t border-neutral-200">
                      <span className="text-neutral-600">Paid by</span>
                      <span className={`font-semibold ${pendingSubmit.paidBy === 'admin' ? 'text-warning-800' : 'text-success-800'}`}>
                        {pendingSubmit.paidBy === 'admin' ? 'Covered by admin' : 'Member paid directly'}
                      </span>
                    </div>
                  </div>

                  {formError && <p className="text-sm text-error-600">{formError}</p>}

                  <div className="flex space-x-2">
                    <button type="button" onClick={() => { setPendingSubmit(null); setFormError(null); }} className="flex-1 border border-neutral-300 rounded-lg px-4 py-3 text-base text-neutral-700">
                      Back
                    </button>
                    <button type="button" onClick={doRecordPayment} disabled={recording} className="flex-1 bg-primary-600 text-neutral-50 rounded-lg px-4 py-3 text-base font-medium disabled:opacity-50">
                      {recording ? 'Recording…' : 'Confirm & Record'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showReadOnly && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-sm">
              <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Payment Details</h3>
                <button onClick={closeReadOnly} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-neutral-600">Amount</span><span className="font-medium">${selectedPayment.amount_received?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Method</span><span className="font-medium capitalize">{selectedPayment.payment_method || '—'}</span></div>
                <div className="flex justify-between"><span className="text-neutral-600">Date</span><span className="font-medium">{selectedPayment.payment_date}</span></div>
                {selectedPayment.reference_number && (
                  <div className="flex justify-between"><span className="text-neutral-600">Reference</span><span className="font-medium">{selectedPayment.reference_number}</span></div>
                )}
                <div className="flex justify-between">
                  <span className="text-neutral-600">Status</span>
                  {selectedPayment.covered_by_admin
                    ? <span className="px-2 py-1 bg-warning-100 text-warning-800 text-xs rounded-full font-medium">COVERED BY ADMIN</span>
                    : <span className="px-2 py-1 bg-success-100 text-success-800 text-xs rounded-full font-medium">PAID ✅</span>}
                </div>
              </div>

              {voidError && <p className="text-sm text-error-600 mt-4">{voidError}</p>}

              {!voidConfirm ? (
                <div className="mt-6 space-y-2">
                  <button onClick={closeReadOnly} className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">
                    Close
                  </button>
                  <button onClick={() => { setVoidConfirm(true); setVoidError(null); }} className="w-full px-4 py-3 text-error-700 text-sm font-medium hover:bg-error-50 rounded-lg">
                    Void this payment
                  </button>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  <p className="text-sm text-neutral-700">
                    Void this payment? It removes the recorded contribution (and reverses the round's funding).
                    The audit trail is kept. This can't be undone if a payout has already been sent for the round.
                  </p>
                  <div className="flex space-x-2">
                    <button onClick={() => setVoidConfirm(false)} disabled={voiding} className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">
                      Cancel
                    </button>
                    <button onClick={handleVoidPayment} disabled={voiding} className="flex-1 px-4 py-3 bg-error-600 text-white rounded-lg text-base font-medium disabled:opacity-50">
                      {voiding ? 'Voiding…' : 'Void payment'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showPayoutForm && selectedPayoutHand && (
          <PayoutForm
            hand={selectedPayoutHand}
            groupData={groupData}
            allHands={hands}
            onClose={() => { setShowPayoutForm(false); setSelectedPayoutHand(null); }}
            onSuccess={() => { setShowPayoutForm(false); setSelectedPayoutHand(null); fetchData(); }}
          />
        )}

        {showReviewModal && reviewHand && reviewPayment && (
          <MemberSubmissionReviewModal
            hand={reviewHand}
            payment={reviewPayment}
            member={reviewMember}
            onClose={() => { setShowReviewModal(false); setReviewHand(null); setReviewPayment(null); setReviewMember(null); }}
            onSuccess={() => { setShowReviewModal(false); setReviewHand(null); setReviewPayment(null); setReviewMember(null); fetchData(); }}
          />
        )}

        {showAssignHandsModal && assigningMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-sm">
              <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
              <h3 className="text-lg font-semibold mb-1">Assign Hands</h3>
              <p className="text-sm text-neutral-500 mb-4">{assigningMember.name}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Number of Hands</label>
                <input
                  type="number" min="1" max="20"
                  value={assignHandsForm.numberOfHands}
                  onChange={e => {
                    const raw = e.target.value;
                    const num = parseInt(raw, 10);
                    const valid = !isNaN(num) && num >= 1 && num <= 20;
                    setAssignHandsForm({ numberOfHands: raw, positions: valid ? Array(num).fill('') : [] });
                  }}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base"
                />
              </div>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {assignHandsForm.positions.map((pos, index) => (
                  <div key={index} className="mb-3">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Hand {index + 1} — Rotation Position</label>
                    <input
                      type="number" min="1" value={pos}
                      onChange={e => {
                        const newPositions = [...assignHandsForm.positions];
                        newPositions[index] = e.target.value;
                        setAssignHandsForm({ ...assignHandsForm, positions: newPositions });
                      }}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-3 text-base"
                    />
                  </div>
                ))}
              </div>
              <div className="flex flex-col space-y-2 mt-6">
                <button onClick={handleAssignHands} className="w-full px-4 py-3 bg-primary-600 text-neutral-50 rounded-lg text-base font-medium">
                  Assign Hands
                </button>
                <button onClick={() => setShowAssignHandsModal(false)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin-advance confirmation dialog */}
        {advancingPayout && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-sm">
              <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
              <h3 className="text-lg font-semibold mb-2">{isFinalRound ? 'Complete the cycle on their behalf?' : 'Advance round on their behalf?'}</h3>
              <p className="text-sm text-neutral-600 mb-4">
                {collectorName} hasn't confirmed receipt of their ${advancingPayout.payout_amount?.toFixed(2)} payout.
                This is recorded as an <strong>admin override</strong>, not a collector confirmation.
              </p>
              <textarea
                value={advanceNote}
                onChange={(e) => setAdvanceNote(e.target.value)}
                placeholder="Reason (optional)"
                rows={2}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-4"
              />
              <div className="flex flex-col space-y-2">
                <button onClick={handleAdminAdvance} className="w-full px-4 py-3 bg-warning-600 text-white rounded-lg text-base font-medium">
                  {isFinalRound ? 'Complete cycle' : 'Advance round'}
                </button>
                <button onClick={() => { setAdvancingPayout(null); setAdvanceNote(''); }} className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showStartCycle && (
          <StartCycleFlow
            groupId={groupId}
            mode={startCycleMode}
            onClose={() => setShowStartCycle(false)}
            onDone={() => { setShowStartCycle(false); fetchData(); }}
          />
        )}

        <div className="mt-6 text-center">
          <ReportProblemButton />
        </div>

      </div>
    </div>
  );
};

export default GroupPaymentDashboard;
